import { Eyebrow } from "@/components/primitives/Eyebrow";
import { Heading } from "@/components/primitives/Heading";
import { QrCode } from "@/components/QrCode";

/**
 * Beacon Mobile-Submit-Hero — Brief 04 Screen 11.
 *
 * Das EINZIGE erlaubte Grün im gesamten Build: das `--signal-ok` Häkchen.
 * Wird gerendert, sobald der Mobile-User eingereicht hat.
 */

interface MobileSubmittedHeroProps {
  /** Zeile hinter der Uhrzeit — Default ist das Vertriebs-Offsite ("Bewertung").
   *  Der Strategietag hat keine Bewertungsphase und übergibt "DIE KI BAUT". */
  kicker?: string;
  submittedAt?: number;
  recap?: string;
  /**
   * Persönlicher, dauerhafter Ergebnis-Link (Code-Login-Offsites). Ohne
   * resultId (Vertrieb/AR-Team-Login) bleibt der Abschnitt weg — es gibt
   * dort kein dauerhaftes Einzelergebnis, sondern nur das Team-Reveal.
   */
  resultUrl?: string;
}

function formatTime(ts?: number): string {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

export function MobileSubmittedHero({
  submittedAt,
  recap,
  resultUrl,
  kicker,
}: MobileSubmittedHeroProps) {
  return (
    <section
      className="beacon-submitted-hero rise-1"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "var(--s-5)",
        padding: "var(--s-7) 0",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          fontSize: 96,
          lineHeight: 1,
          color: "var(--signal-ok)",
          fontWeight: 700,
        }}
      >
        ✓
      </span>
      <Eyebrow accent>EINGEREICHT</Eyebrow>
      <Heading level="h1">Eingereicht.</Heading>
      <span
        className="mono"
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          letterSpacing: "var(--tracking-meta)",
          textTransform: "uppercase",
          color: "var(--paper-400)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formatTime(submittedAt) || "—"} · {kicker ?? "BEWERTUNG STARTET GLEICH"}
      </span>
      {recap && (
        <p
          style={{
            color: "var(--paper-500)",
            fontSize: "var(--m-body)",
            lineHeight: 1.5,
            margin: 0,
          }}
        >
          {recap}
        </p>
      )}
      {resultUrl && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "var(--s-3)",
            marginTop: "var(--s-3)",
          }}
        >
          <p
            style={{
              color: "var(--paper-500)",
              fontSize: "var(--m-body)",
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            Dein Prototyp entsteht — unter diesem Link findest du ihn
            dauerhaft.
          </p>
          <QrCode value={resultUrl} klein />
          <a
            href={resultUrl}
            className="mono"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--accent-blue)",
              wordBreak: "break-all",
            }}
          >
            {resultUrl}
          </a>
        </div>
      )}
    </section>
  );
}
